import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { KeyRound, Loader2, ShieldCheck, LogOut } from 'lucide-react';

/**
 * Hard gate shown after admin-initiated password reset.
 * The user is logged in (so we have a token) but `force_password_change=true`.
 * They cannot access any protected route until they set a new password.
 */
const ForcePasswordChange = () => {
  const { user, updateUser, logout } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/change-temp-password', { new_password: newPassword });
      // Update local user object so the gate releases
      updateUser({ ...user, force_password_change: false });
      toast.success('Password updated. Welcome back!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4" data-testid="force-password-change-page">
      <Card className="w-full max-w-md shadow-xl border-amber-200">
        <CardHeader className="text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mb-2">
            <KeyRound className="h-7 w-7 text-amber-600" />
          </div>
          <CardTitle className="text-xl">Set a New Password</CardTitle>
          <CardDescription>
            Your administrator issued a temporary password. Choose a permanent one below before continuing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoFocus
                required
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Type it again"
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              disabled={submitting || !newPassword || !confirmPassword}
              data-testid="submit-new-password-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Update Password
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-gray-500"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Cancel and Log Out
            </Button>
          </form>
          <p className="text-xs text-center text-gray-400 mt-4">
            Logged in as <span className="font-mono">{user?.email}</span>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForcePasswordChange;
